import { Link } from 'react-router-dom';

const updatedAt = 'May 22, 2026';

export function TermsPage() {
  return (
    <main className="min-h-screen bg-slate-50 text-slate-900">
      <div className="mx-auto max-w-4xl px-5 py-10 sm:py-14">
        <Link to="/" className="inline-flex items-center gap-2 text-sm font-semibold text-squishy-primary hover:underline">
          Back to Squishy School
        </Link>

        <header className="mt-6 rounded-2xl border bg-white p-6 shadow-sm sm:p-8">
          <p className="text-sm font-semibold uppercase tracking-wide text-squishy-primary">Terms of Service</p>
          <h1 className="mt-2 text-3xl font-bold text-squishy-dark sm:text-4xl">Squishy School Terms of Service</h1>
          <p className="mt-3 text-sm text-slate-500">Last updated: {updatedAt}</p>
          <p className="mt-5 leading-7 text-slate-700">
            These Terms of Service ("Terms") govern your access to and use of Squishy School, operated by SignOut LLC ("Squishy School," "we," "us," or "our"), including our website, mobile applications for iOS and Android, and all related services (collectively, the "Service"). By creating an account or using the Service, you agree to these Terms.
          </p>
        </header>

        <div className="mt-6 space-y-5">
          <Section title="1. Eligibility">
            <p>
              You must be at least 13 years of age to use Squishy School. If you are under 18, you represent that a parent or legal guardian has reviewed and agrees to these Terms on your behalf. We do not knowingly collect personal information from children under 13. If you are located in the European Economic Area, you must be at least 16 years of age, or the minimum age required by applicable law in your country.
            </p>
          </Section>

          <Section title="2. Accounts">
            <p>
              You are responsible for maintaining the confidentiality of your account credentials and for all activity under your account. You agree to provide accurate, current, and complete information during registration and to update your information as needed. You must notify us promptly if you suspect unauthorized access to your account. We reserve the right to suspend or terminate accounts that violate these Terms.
            </p>
          </Section>

          <Section title="3. Acceptable Use">
            <p>
              You agree to use Squishy School only for lawful educational purposes. You may not:
            </p>
            <ul>
              <li>Upload, post, or transmit content that is unlawful, harmful, threatening, abusive, harassing, defamatory, vulgar, obscene, or otherwise objectionable.</li>
              <li>Upload content that infringes any patent, trademark, trade secret, copyright, or other proprietary rights of any party.</li>
              <li>Attempt to reverse engineer, decompile, disassemble, or otherwise attempt to discover the source code of the Service.</li>
              <li>Use the Service to cheat on exams, submit AI-generated content as your own original work in academic settings, or violate academic integrity policies.</li>
              <li>Use automated scripts, bots, or scrapers to access the Service without our written permission.</li>
              <li>Circumvent, disable, or otherwise interfere with security features of the Service.</li>
              <li>Impersonate another person or entity, or falsely state or otherwise misrepresent your affiliation with a person or entity.</li>
              <li>Harass, bully, or intimidate other users through comments, discussions, or any other feature.</li>
            </ul>
          </Section>

          <Section title="4. User Content">
            <p>
              You retain ownership of content you upload to Squishy School ("User Content"), including notes, documents, images, audio, and text. By uploading User Content, you grant us a non-exclusive, worldwide, royalty-free license to use, process, store, display, and transform your User Content solely for the purpose of providing, improving, and operating the Service. This includes processing your content through AI systems to generate questions, explanations, and study materials.
            </p>
            <p>
              You represent that you have all necessary rights to upload your User Content and that your uploads do not violate any third-party rights or applicable laws.
            </p>
          </Section>

          <Section title="5. AI-Generated Content">
            <p>
              Squishy School uses artificial intelligence to generate study questions, explanations, flashcards, and related educational content. While we strive for accuracy through multi-layer validation, AI-generated content may contain errors, omissions, or inaccuracies. AI-generated content is provided for educational and study purposes only and should not be relied upon as medical advice, legal advice, or professional guidance. You are responsible for verifying the accuracy of AI-generated content independently.
            </p>
          </Section>

          <Section title="6. Subscriptions and In-App Purchases">
            <p>
              Squishy School offers free and paid subscription tiers. Paid subscriptions ("Pro") are available exclusively through in-app purchases on iOS (Apple App Store) and Android (Google Play Store).
            </p>
            <ul>
              <li><strong>Free trial:</strong> New subscribers may receive a free trial period. If you do not cancel before the trial ends, your subscription will automatically renew at the applicable price.</li>
              <li><strong>Billing:</strong> All billing is handled by the Apple App Store or Google Play Store. We do not process payments directly.</li>
              <li><strong>Cancellation:</strong> You may cancel your subscription at any time through your Apple App Store or Google Play Store account settings. Cancellation takes effect at the end of the current billing period.</li>
              <li><strong>Refunds:</strong> Refund requests are subject to the policies of Apple or Google. Contact Apple Support or Google Play Support for refund inquiries.</li>
              <li><strong>Price changes:</strong> We may change subscription pricing. Any price changes will be communicated in advance and will apply to the next billing cycle after notice.</li>
            </ul>
          </Section>

          <Section title="7. Intellectual Property">
            <p>
              The Service, including its design, features, code, branding, logos, and non-user content, is owned by SignOut LLC and is protected by copyright, trademark, and other intellectual property laws. You may not copy, modify, distribute, sell, or create derivative works of any part of the Service without our prior written consent. All trademarks, service marks, and trade names are the property of their respective owners.
            </p>
          </Section>

          <Section title="8. Community Features">
            <p>
              Squishy School includes community features such as question discussions, comments, voting, and leaderboards. Content you post in community features may be visible to other users. We reserve the right to moderate, remove, or restrict community content that violates these Terms. You are responsible for the content you post and agree not to post content that is harmful, misleading, or violates any law.
            </p>
          </Section>

          <Section title="9. Privacy">
            <p>
              Your use of the Service is also governed by our <Link to="/privacy" className="text-squishy-primary hover:underline">Privacy Policy</Link>, which explains how we collect, use, and protect your information. By using the Service, you consent to our data practices as described in the Privacy Policy.
            </p>
          </Section>

          <Section title="10. Disclaimers">
            <p>
              THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, ACCURACY, OR NON-INFRINGEMENT. WE DO NOT WARRANT THAT THE SERVICE WILL BE UNINTERRUPTED, ERROR-FREE, OR SECURE, OR THAT ANY CONTENT (INCLUDING AI-GENERATED CONTENT) IS ACCURATE, COMPLETE, OR RELIABLE. USE OF THE SERVICE IS AT YOUR OWN RISK.
            </p>
          </Section>

          <Section title="11. Limitation of Liability">
            <p>
              TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, SIGNOUT LLC AND ITS OFFICERS, DIRECTORS, EMPLOYEES, AND AGENTS SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS, DATA, USE, GOODWILL, OR OTHER INTANGIBLE LOSSES, RESULTING FROM (A) YOUR ACCESS TO OR USE OF, OR INABILITY TO ACCESS OR USE, THE SERVICE; (B) ANY CONTENT OBTAINED FROM THE SERVICE, INCLUDING AI-GENERATED CONTENT; (C) UNAUTHORIZED ACCESS TO OR ALTERATION OF YOUR DATA; OR (D) ANY OTHER MATTER RELATING TO THE SERVICE. OUR TOTAL AGGREGATE LIABILITY SHALL NOT EXCEED THE GREATER OF $50 USD OR THE AMOUNT YOU PAID US IN THE 12 MONTHS PRIOR TO THE CLAIM.
            </p>
          </Section>

          <Section title="12. Indemnification">
            <p>
              You agree to indemnify, defend, and hold harmless SignOut LLC and its officers, directors, employees, and agents from and against any claims, liabilities, damages, losses, and expenses, including reasonable attorneys' fees, arising out of or in any way connected with your access to or use of the Service, your User Content, or your violation of these Terms.
            </p>
          </Section>

          <Section title="13. Termination">
            <p>
              We may suspend or terminate your access to the Service at any time, with or without cause, with or without notice. Upon termination, your right to use the Service ceases immediately. Provisions of these Terms that by their nature should survive termination shall survive, including ownership, disclaimers, indemnification, and limitations of liability.
            </p>
          </Section>

          <Section title="14. Governing Law and Dispute Resolution">
            <p>
              These Terms are governed by the laws of the State of Delaware, United States, without regard to conflict of law principles. Any dispute arising from these Terms or the Service shall be resolved through binding arbitration under the rules of the American Arbitration Association, except that either party may seek injunctive or equitable relief in any court of competent jurisdiction. You agree to resolve disputes individually and waive any right to participate in a class action or class-wide arbitration.
            </p>
          </Section>

          <Section title="15. International Users">
            <p>
              The Service is operated from the United States. If you access the Service from outside the United States, you do so at your own risk and are responsible for compliance with local laws. For users in the European Economic Area (EEA), United Kingdom, or Switzerland, nothing in these Terms limits your rights under applicable consumer protection laws, including the right to withdraw from a contract within 14 days of purchase (except where the digital content has been fully performed with your prior consent and acknowledgment). For users in Canada, these Terms are subject to applicable federal and provincial consumer protection legislation.
            </p>
          </Section>

          <Section title="16. Changes to These Terms">
            <p>
              We may update these Terms from time to time. When we make material changes, we will update the date at the top of this page and provide additional notice when required by law, app store policy, or applicable regulation. Your continued use of the Service after changes take effect constitutes acceptance of the revised Terms.
            </p>
          </Section>

          <Section title="17. Contact Us">
            <p>
              For questions about these Terms, contact us at <a href="mailto:support@squishyschool.com" className="text-squishy-primary hover:underline">support@squishyschool.com</a>.
            </p>
            <p>
              SignOut LLC operates Squishy School.
            </p>
          </Section>
        </div>
      </div>
    </main>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="rounded-2xl border bg-white p-6 shadow-sm">
      <h2 className="text-xl font-bold text-squishy-dark">{title}</h2>
      <div className="privacy-copy mt-3 space-y-3 text-sm leading-7 text-slate-700">
        {children}
      </div>
    </section>
  );
}
